import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';

import { FeatureGateService } from 'src/shared/services/feature-gate.service';

import { RentalUnitsService, RentalUnit } from 'src/shared/services/rental-units.service';
import { RoomOpsService, SharedExpense, WaitlistEntry, UpkeepTask } from 'src/shared/services/room-ops.service';

// Precomputed row view models — status colors resolved up front so the
// template only binds properties (CLAUDE.md: no template function calls).
export interface SharedExpenseView {
  expense: SharedExpense;
  statusColor: string;
}

export interface WaitlistEntryView {
  entry: WaitlistEntry;
  statusColor: string;
}

@Component({
  templateUrl: 'room-operations.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RoomOperationsPage implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private featureGate = inject(FeatureGateService);
  private unitsService = inject(RentalUnitsService);
  private roomOpsService = inject(RoomOpsService);

  readonly windowService = inject(WindowService);

  isRightSideContent = true;
  unitId: number;
  unit: RentalUnit;
  occupancyLabel = '';
  sharedExpenses: SharedExpenseView[] = [];
  waitlist: WaitlistEntryView[] = [];
  roomUpkeepTasks: UpkeepTask[] = [];
  commonAreaUpkeepTasks: UpkeepTask[] = [];
  loaded = false;
  errorMessage: string;
  canLogTurnoverClean = false;
  private vacantRoomId: number | null = null;
  private panelClosedSubscription: Subscription;

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    const params = await this.storage.retrieve('dialog-params');
    this.unitId = this.navCtrl.getParam('unitId') ?? params?.unitId;
    await this.refresh();
    if (this.isRightSideContent) {
      this.rightSidePanelService.setDialogPageTitle('Room Operations');
    }
    setTimeout(() => {
      this.loaded = true;
      this.cdr.markForCheck();
    });
  }

  async refresh(): Promise<void> {
    if (!this.unitId) {
      this.errorMessage = 'No unit selected.';
      return;
    }
    try {
      this.unit = await this.unitsService.getUnit(this.unitId);
      if (!this.unit) {
        this.errorMessage = 'Unit not found.';
        return;
      }
      this.occupancyLabel = this.unitsService.occupancySummary(this.unit).label;
      const [expenses, waitlist, upkeepTasks] = await Promise.all([
        this.roomOpsService.getSharedExpenses(this.unitId),
        this.roomOpsService.getWaitlist(this.unitId),
        this.roomOpsService.getUpkeepTasks(this.unitId),
      ]);
      this.sharedExpenses = expenses.map(expense => ({
        expense,
        statusColor: this.roomOpsService.expenseStatusColor(expense.status),
      }));
      this.waitlist = waitlist.map(entry => ({
        entry,
        statusColor: this.roomOpsService.waitlistStatusColor(entry.status),
      }));
      // A task is room-scoped when it carries space_id, or (for legacy tasks
      // seeded before space_id existed) when its area string names a room.
      const roomNames = new Set((this.unit.spaces || []).filter(s => s.type === 'room').map(s => s.name));
      this.roomUpkeepTasks = upkeepTasks.filter(t => !!t.space_id || roomNames.has(t.area));
      this.commonAreaUpkeepTasks = upkeepTasks.filter(t => !t.space_id && !roomNames.has(t.area));
      const vacantRoom = (this.unit.spaces || []).find(s => s.type === 'room' && s.state === 'Vacant');
      this.vacantRoomId = vacantRoom?.id ?? null;
      this.canLogTurnoverClean = !!vacantRoom;
    } catch {
      this.errorMessage = 'Could not load room operations for this unit.';
    }
    this.cdr.markForCheck();
  }

  // --- Actions — no backend behind these yet, so both hand off to the account
  // manager (arrow-fn props so they bind to tidy-button / tidy-text [action]) ---
  addWaitlistEntry = () => this.featureGate.gate();

  logTurnoverClean = async () => {
    this.featureGate.gate();
  };

  goToPage(url: string, params: any, component: any) {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      this.rightSidePanelService.navigateTo(url, params, component);
      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => this.cdr.markForCheck());
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }
}
